/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { BusinessPartnerUserPermissions } from '@msdyn365-commerce/retail-proxy';

/**
 * Union of the boolean permission flags on {@link BusinessPartnerUserPermissions} that gate B2B actions.
 * The identity/metadata fields (`ContactPersonId`, `PermissionGroupId`, `ExtensionProperties`) are excluded.
 */
export type B2BPermission = Exclude<keyof BusinessPartnerUserPermissions, 'ContactPersonId' | 'PermissionGroupId' | 'ExtensionProperties'>;

/**
 * Named B2B permission flags. Callers pass `B2bPermission.AllowViewInvoices` to
 * {@link isB2bActionPermitted} instead of a raw string, so typos surface at compile time.
 */
export const B2bPermission = {
    AllowUserCreation: 'AllowUserCreation',
    AllowDisablingUsers: 'AllowDisablingUsers',
    AllowViewInvoices: 'AllowViewInvoices',
    AllowOnAccountUse: 'AllowOnAccountUse',
    AllowOrganizationOrderHistory: 'AllowOrganizationOrderHistory',
    AllowOrganizationOrderTemplateManagement: 'AllowOrganizationOrderTemplateManagement'
} as const;

/**
 * Options for {@link isB2bActionPermitted}.
 */
export interface IB2bActionPermissionCheck {
    /**
     * Whether the current user is governed by the B2BContact permission model
     * (`requestContext.user.isB2bContact`). A missing value is treated as `false`, so callers can pass
     * the optional flag directly. When falsy, the legacy admin behavior is used.
     */
    isB2bContact?: boolean;

    /**
     * The legacy admin flag used before granular permissions existed
     * (for example `customer.IsB2bAdmin` or `businessPartnerUser.IsAdmin`). A missing value is treated
     * as `false`, so callers can pass the optional flag directly.
     */
    isLegacyAdmin?: boolean;

    /**
     * Granular permissions read from `BusinessPartnerUser.Permissions`, when available.
     */
    permissions?: BusinessPartnerUserPermissions;

    /**
     * The granular permission(s) that grant the action; the action is allowed when ANY of them is granted.
     *
     * Omit this for admin-only actions that have no matching granular permission. In that case the action
     * falls back to the B2B super user (`IsSuperUser`), which is the granular-model equivalent of the
     * legacy `IsB2bAdmin`. Do not pass an unrelated granular permission just to fill this in — when in
     * doubt, leave it out and let the super-user check govern.
     */
    required?: B2BPermission | B2BPermission[];
}

/**
 * Determines whether the current B2B user may perform a permission-gated action.
 *
 * Design:
 * - When the B2BContact permission model is OFF (`isB2bContact` is falsy), the result is the legacy
 *   admin flag, preserving the exact pre-existing behavior (no regression).
 * - When it is ON, granular permissions govern: a super user is always allowed, otherwise the action
 *   is allowed only when one of the `required` permissions is granted. If `required` is omitted, the
 *   action is super-user-only (the granular equivalent of the legacy admin flag).
 * - When it is ON but no permissions payload is available yet (older CSU during rollout), fall back to
 *   the legacy admin flag so administrators are not locked out.
 *
 * @param check The permission check inputs.
 * @returns `true` if the action is permitted, otherwise `false`.
 */
export function isB2bActionPermitted(check: IB2bActionPermissionCheck): boolean {
    const { isB2bContact, isLegacyAdmin, permissions, required } = check;

    // Feature OFF: preserve legacy admin behavior exactly.
    if (!isB2bContact) {
        return isLegacyAdmin === true;
    }

    // Feature ON but permissions not yet returned by the CSU: fall back to legacy admin.
    if (!permissions) {
        return isLegacyAdmin === true;
    }

    // Feature ON: granular permissions govern.
    if (permissions.IsSuperUser === true) {
        return true;
    }

    // No specific granular permission for this action: super-user-only (handled above), so deny others.
    const requiredPermissions = required === undefined ? [] : Array.isArray(required) ? required : [required];
    return requiredPermissions.some(permission => permissions[permission] === true);
}
